import * as React from 'react';
const { createContext, useContext, useState, useEffect } = React;
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import Navbar           from './components/Navbar.jsx';
import Footer           from './components/Footer.jsx';
import BuscadorPublico  from './pages/BuscadorPublico.jsx';
import IndicePropiedad  from './pages/IndicePropiedad.jsx';
import Solicitudes      from './pages/Solicitudes.jsx';
import Usuarios         from './pages/Usuarios.jsx';
import PortalTramites   from './pages/PortalTramites.jsx';
import ConsolaFuncionario from './pages/ConsolaFuncionario.jsx';
import TramiteDetalle from './pages/TramiteDetalle.jsx';
import GestionSolicitudes from './pages/GestionSolicitudes.jsx';
import FirmaQR          from './pages/FirmaQR.jsx';
import CreacionIndice from "./pages/CreacionIndice.jsx";
import EstadoSolicitud from "./pages/EstadoSolicitud.jsx";
import IngresoTramites from "./pages/IngresoTramites.jsx";
import ValidarCorreoTramite from "./pages/ValidarCorreoTramite.jsx";
import ResultadosBuscador from "./pages/ResultadosBuscador.jsx";
import IngresoIndice from "./pages/Indices/IngresoIndice.jsx";
import TomadorFojas from "./pages/Indices/TomadorFojas.jsx";
import FolioReal from "./pages/Indices/FolioReal.jsx";
import ConsultaSociedades from "./pages/Indices/ConsultaSociedades.jsx";
import ValidarCorreoSolicitud from "./pages/ValidarCorreoSolicitud.jsx";
import Contacto from "./pages/Contacto.jsx";
import QuienesSomos from "./pages/QuienesSomos.jsx";
import AdminQuienesSomos from "./pages/AdminQuienesSomos.jsx";
import AdminComunicado from "./pages/AdminComunicado.jsx";
import HistorialTramite from "./pages/HistorialTramite.jsx";
import HistorialSolicitud from "./pages/HistorialSolicitud.jsx";
import ArchiveroPublico from "./pages/ArchiveroPublico.jsx";
import ValidarArchivero from "./pages/ValidarArchivero.jsx";
import HistorialArchivero from "./pages/HistorialArchivero.jsx";
import GestionArchivero from "./pages/GestionArchivero.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import VerificarCertificado from "./pages/VerificarCertificado.jsx";
import ChatWidget from "./components/Chat/ChatWidget.jsx";
import DashboardFuncionario from "./pages/Dashboard/DashboardFuncionario.jsx";
import DashboardImpacto from "./pages/DashboardImpacto.jsx";
import HerramientasCotizacion from "./pages/HerramientasCotizacion.jsx";


// ── Auth Context ─────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('cbr_auth');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem('cbr_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    localStorage.setItem('cbr_auth', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('cbr_auth');
    setUser(null);
  };

  const hasRole = (role) => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase().trim();
    if (userRole === 'super_admin') return true; // 👑 Super Admin tiene acceso total
    if (!role) return true;
    const allowedRoles = role.split(',').map(r => r.toLowerCase().trim());
    return allowedRoles.includes(userRole);
  };
  const isAdmin = () => hasRole('admin');
  const isStaff = () => ['super_admin', 'admin', 'boss', 'staff'].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasRole, isAdmin, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Protected Route ──────────────────────────────────────────────────────────
function ProtectedRoute({ children, requiredRole, requiredEmail }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner spinner-lg" />
        <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>Verificando sesión...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location, authRequired: true }} replace />;
  }

  if (requiredEmail && user.email?.toLowerCase().trim() !== requiredEmail.toLowerCase().trim()) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="glass-card" style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <h3>Acceso Restringido</h3>
          <p className="text-muted" style={{ margin: '1rem 0' }}>
            Esta herramienta está reservada exclusivamente para el administrador principal ({requiredEmail}).
          </p>
        </div>
      </div>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="glass-card" style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <h3>Acceso Denegado</h3>
          <p className="text-muted" style={{ margin: '1rem 0' }}>
            No tienes permisos para acceder a este módulo.
          </p>
          <span className="badge badge-error">Rol requerido: {requiredRole}</span>
        </div>
      </div>
    );
  }

  return children;
}

/**
 * ReCaptchaBadgeManager — Oculta el badge en rutas administrativas
 * para evitar ruido visual y errores.
 */
function ReCaptchaBadgeManager() {
  const location = useLocation();
  const isAdminPath = [
    '/usuarios',
    '/funcionario', 
    '/firma',
    '/tramites',
    '/tramite',
    '/admin/quienes-somos'
  ].some(path => location.pathname.startsWith(path));

  useEffect(() => {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      badge.style.visibility = isAdminPath ? 'hidden' : 'visible';
      badge.style.opacity = isAdminPath ? '0' : '1';
      badge.style.pointerEvents = isAdminPath ? 'none' : 'auto';
    }
  }, [location.pathname, isAdminPath]);

  return null;
}

// ── App Shell ────────────────────────────────────────────────────────────────
function AppShell() {
  return (
    <div className="page-wrapper">
      <ReCaptchaBadgeManager />
      <Navbar />
      <main style={{ flex: 1, paddingTop: '80px' }}>
        <Routes>
          {/* Públicas */}
          <Route path="/"                 element={<BuscadorPublico />} />
          <Route path="/resultados"       element={<ResultadosBuscador />} />
          <Route path="/indicepropiedad"  element={<IndicePropiedad />} />
          <Route path="/indice-sociedades" element={<ConsultaSociedades />} />
          <Route path="/indice-comercio"   element={<ConsultaSociedades />} />
          <Route path="/solicitudes"      element={<Solicitudes />} />
          <Route path="/estadosolicitud"  element={<EstadoSolicitud />} />
          <Route path="/archivero"          element={<ArchiveroPublico />} />
          <Route path="/archivero/validar-correo/:token" element={<ValidarArchivero />} />
          <Route path="/solicitudes/validar-correo/:token" element={<ValidarCorreoSolicitud />} />
          <Route path="/ingresotramite"  element={<IngresoTramites />} />
          <Route path="/tramites/seguimiento" element={<IngresoTramites />} />
          <Route path="/tramites/validar-correo/:token" element={<ValidarCorreoTramite />} />
          <Route path="/contacto"  element={<Contacto />} />
          <Route path="/quienes-somos"  element={<QuienesSomos />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protegidas — cualquier usuario autenticado */}
          <Route path="/tramites" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><PortalTramites /></ProtectedRoute>
          } />
          <Route path="/verificar" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><VerificarCertificado /></ProtectedRoute>
          } />

          {/* Protegidas — solo admin */}
          <Route path="/gestion-solicitudes" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><GestionSolicitudes /></ProtectedRoute>
          } />
          <Route path="/gestion-archivero" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><GestionArchivero /></ProtectedRoute>
          } />
          <Route path="/archivero/historial/:id" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><HistorialArchivero /></ProtectedRoute>
          } />
          <Route path="/usuarios" element={
            <ProtectedRoute requiredRole="admin"><Usuarios /></ProtectedRoute>
          } />

          {/* Protegidas — solo staff (ahora incluye Boss y Super Admin) */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><DashboardFuncionario /></ProtectedRoute>
          } />
          <Route path="/dashboard-impacto" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><DashboardImpacto /></ProtectedRoute>
          } />
          <Route path="/funcionario" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><ConsolaFuncionario /></ProtectedRoute>
          } />
          <Route path="/tramite/:id" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><TramiteDetalle /></ProtectedRoute>
          } />
          <Route path="/tramite/historial/:id" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><HistorialTramite /></ProtectedRoute>
          } />
          <Route path="/solicitud/historial/:id" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><HistorialSolicitud /></ProtectedRoute>
          } />
          <Route path="/firma" element={
            <ProtectedRoute requiredRole="boss,staff"><FirmaQR /></ProtectedRoute>
          } />
          <Route path="/creacion-indice" element={
            <ProtectedRoute requiredRole="boss,staff"><CreacionIndice /></ProtectedRoute>
          } />
          <Route path="/ingreso-indice" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><IngresoIndice /></ProtectedRoute>
          } />
          <Route path="/tomador-fojas" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><TomadorFojas /></ProtectedRoute>
          } />
          <Route path="/folio-real" element={
            <ProtectedRoute requiredRole="admin,boss,staff"><FolioReal /></ProtectedRoute>
          } />
          <Route path="/admin/quienes-somos" element={
            <ProtectedRoute requiredRole="admin,staff"><AdminQuienesSomos /></ProtectedRoute>
          } />
          <Route path="/admin/comunicado" element={
            <ProtectedRoute requiredRole="admin"><AdminComunicado /></ProtectedRoute>
          } />
          <Route path="/admin/cotizaciones" element={
            <ProtectedRoute requiredEmail="admin@cbr.cl"><HerramientasCotizacion /></ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

      </main>
      <ChatWidget />
      <Footer />

    </div>
  );
}

// ── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeODZEsAAAAAGGmpmGvgabNdg_63PDdy-xo5lxa";

  useEffect(() => {
    const root = document.documentElement;
    const primary = import.meta.env.VITE_THEME_PRIMARY;
    const primaryDark = import.meta.env.VITE_THEME_PRIMARY_DARK;
    const accent = import.meta.env.VITE_THEME_ACCENT;

    if (primary) {
      root.style.setProperty('--primary', primary);
    }
    if (primaryDark) {
      root.style.setProperty('--primary-dark', primaryDark);
    }
    if (accent) {
      root.style.setProperty('--accent', accent);
    }
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1a1e38',
                color: '#ffffff',
                border: '1px solid rgba(184,115,51,0.35)',
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.9rem',
              },
              success: { iconTheme: { primary: '#22c55e', secondary: '#1a1e38' } },
              error:   { iconTheme: { primary: '#ef4444', secondary: '#1a1e38' } },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </GoogleReCaptchaProvider>
  );
}
